import {
  ed25519,
  edwardsToMontgomeryPriv,
  edwardsToMontgomeryPub,
  x25519,
} from '@noble/curves/ed25519'
import { hkdf } from '@noble/hashes/hkdf'
import { randomBytes } from '@noble/hashes/utils'
import { decryptAesGcm, encryptAesGcm } from './aes.js'
import { CRYPTO_CONSTANTS } from './constants.js'
import { sha256, textEncoder } from './utils.js'

const kdfInfo = textEncoder.encode('workers communication message')

const { IV_LENGTH, AES_KEY_LENGTH } = CRYPTO_CONSTANTS

/**
 * Performs Elliptic Curve Diffie-Hellman (ECDH) key exchange
 */
export const getECDHKey = (
  ed25519PrivateKey: Uint8Array,
  otherSideEd25519PublicKey: Uint8Array,
): Uint8Array => {
  const x25519PrivateKey = edwardsToMontgomeryPriv(ed25519PrivateKey)
  const x25519PublicKey = edwardsToMontgomeryPub(otherSideEd25519PublicKey)

  return x25519.getSharedSecret(x25519PrivateKey, x25519PublicKey)
}

/**
 * Derives a symmetric encryption key using HKDF
 */
export const getSymmetricEncryptionKey = (
  ed25519PrivateKey: Uint8Array,
  otherSideEd25519PublicKey: Uint8Array,
  salt: Uint8Array,
): Uint8Array => {
  const ecdhKey = getECDHKey(ed25519PrivateKey, otherSideEd25519PublicKey)

  return hkdf(sha256, ecdhKey, salt, kdfInfo, AES_KEY_LENGTH)
}

/**
 * Encrypts a payload using end-to-end encryption
 */
export const encrypt = async (
  ed25519PrivateKey: Uint8Array,
  otherSideEd25519PublicKey: Uint8Array,
  payload: Uint8Array,
  salt: Uint8Array,
): Promise<Uint8Array> => {
  const symmetricKey = getSymmetricEncryptionKey(ed25519PrivateKey, otherSideEd25519PublicKey, salt)

  const iv = randomBytes(IV_LENGTH)

  const ciphertext = await encryptAesGcm(payload, symmetricKey, iv)

  const result = new Uint8Array(iv.length + ciphertext.length)
  result.set(iv, 0)
  result.set(ciphertext, iv.length)

  return result
}

/**
 * Decrypts an encrypted payload
 */
export const decrypt = async (
  ed25519PrivateKey: Uint8Array,
  otherSideEd25519PublicKey: Uint8Array,
  payload: Uint8Array,
  salt: Uint8Array,
): Promise<Uint8Array> => {
  const symmetricKey = getSymmetricEncryptionKey(ed25519PrivateKey, otherSideEd25519PublicKey, salt)

  const iv = payload.slice(0, IV_LENGTH)
  const ciphertext = payload.slice(IV_LENGTH)

  return decryptAesGcm(ciphertext, symmetricKey, iv)
}

/**
 * Signs a message using Ed25519
 */
export const sign = (privateKey: Uint8Array | string, message: Uint8Array | string): Uint8Array => {
  return ed25519.sign(message as Uint8Array, privateKey as Uint8Array)
}

/**
 * Verifies an Ed25519 signature
 */
export const verify = (
  publicKey: Uint8Array | string,
  message: Uint8Array | string,
  signature: Uint8Array | string,
): boolean => {
  return ed25519.verify(signature as Uint8Array, message as Uint8Array, publicKey as Uint8Array)
}

/**
 * Derives the public key from a private key
 */
export const getPublicKey = (privateKey: Uint8Array | string): Uint8Array => {
  return ed25519.getPublicKey(privateKey as Uint8Array)
}
